import { useEffect, useRef, useState } from "react";

export const getScoreStatus = (score) => {
  const s = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
  if (s >= 75) return { score: s, key: "strong", label: "STRONG DEAL", short: "Strong", color: "var(--rd-strong)" };
  if (s >= 50) return { score: s, key: "fair", label: "FAIR DEAL", short: "Fair", color: "var(--rd-fair)" };
  return { score: s, key: "weak", label: "WEAK DEAL", short: "Weak", color: "var(--rd-weak)" };
};

const SIZES = {
  sm: { px: 64, stroke: 6, num: "text-lg", sub: "text-[8px]", label: "text-[10.5px]" },
  md: { px: 96, stroke: 8, num: "text-2xl", sub: "text-[10px]", label: "text-[10px]" },
  lg: { px: 140, stroke: 10, num: "text-4xl", sub: "text-xs", label: "text-[11px]" },
  xl: { px: 184, stroke: 12, num: "text-6xl", sub: "text-sm", label: "text-xs" },
};

const useInView = (ref) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
};

/** Compact pill: "87 Strong" — for dense lists/tables. */
const TONES = {
  light: { num: "text-rd-text", sub: "text-rd-muted", track: "#E5E7EB", badge: "border-rd-border bg-white text-rd-text" },
  dark: { num: "text-white", sub: "text-white/50", track: "rgba(255,255,255,0.10)", badge: "border-white/10 bg-white/5 text-white" },
  app: { num: "text-foreground", sub: "text-muted-foreground", track: "hsl(var(--border))", badge: "border-border bg-secondary/60 text-foreground" },
};
const pickTone = (tone, dark) => TONES[dark ? "dark" : tone] || TONES.light;

export function DealScoreBadge({ score, className = "", dark = false, tone = "light" }) {
  const st = getScoreStatus(score);
  const t = pickTone(tone, dark);
  return (
    <span
      role="img"
      aria-label={`Deal Score ${st.score} out of 100 — ${st.short} Deal`}
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-bold font-mono border ${t.badge} ${className}`}
      data-testid="deal-score-badge"
    >
      <span className="w-2 h-2 rounded-full" style={{ background: st.color }} aria-hidden="true" />
      {st.score}
      <span className="font-sans font-semibold" style={{ color: st.color }}>{st.short}</span>
    </span>
  );
}

/** Circular Deal Score gauge — RealixData signature element. Animates on viewport entry. */
export function DealScoreGauge({ score, size = "md", showLabel = true, dark = false, tone = "light", className = "", trackColor }) {
  const st = getScoreStatus(score);
  const t = pickTone(tone, dark);
  const cfg = SIZES[size] || SIZES.md;
  const ref = useRef(null);
  const inView = useInView(ref);
  const r = (cfg.px - cfg.stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - (inView ? st.score : 0) / 100);

  return (
    <div
      ref={ref}
      role="img"
      aria-label={`Deal Score ${st.score} out of 100 — ${st.short} Deal`}
      className={`inline-flex flex-col items-center ${className}`}
      data-testid="deal-score-gauge"
      data-score={st.score}
      data-status={st.key}
    >
      <div className="relative" style={{ width: cfg.px, height: cfg.px }}>
        <svg width={cfg.px} height={cfg.px} viewBox={`0 0 ${cfg.px} ${cfg.px}`} aria-hidden="true" focusable="false">
          <circle cx={cfg.px / 2} cy={cfg.px / 2} r={r} fill="none" stroke={trackColor || t.track} strokeWidth={cfg.stroke} />
          <circle
            className="rd-gauge-arc"
            cx={cfg.px / 2} cy={cfg.px / 2} r={r} fill="none"
            stroke={st.color} strokeWidth={cfg.stroke} strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={offset}
            transform={`rotate(-90 ${cfg.px / 2} ${cfg.px / 2})`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className={`font-mono font-bold ${cfg.num} ${t.num}`}>{st.score}</span>
          <span className={`${cfg.sub} font-semibold mt-0.5 ${t.sub}`}>/100</span>
        </div>
      </div>
      {showLabel && (
        <span className={`mt-2 ${cfg.label} font-bold tracking-[0.14em] uppercase whitespace-nowrap`} style={{ color: st.color }}>
          {st.label}
        </span>
      )}
    </div>
  );
}

export default DealScoreGauge;
